/**
 * AuthProvider — cookie session (`/api/v2/auth`).
 *
 * The SPA holds no tokens. The API mints an access and a refresh token and
 * stores both as `HttpOnly` cookies; this provider only ever asks the API what
 * session those cookies represent.
 *
 * Boot flow:
 *   1. `GET /session` — safe to call unconditionally, answers 200 for an
 *      anonymous caller, and is the only source of the CSRF token.
 *   2. If the answer is `refreshable`, spend the refresh cookie — retrying a few
 *      times on a failure that carries no verdict. That state exists because
 *      JavaScript cannot see the `HttpOnly` refresh cookie: without the attempt,
 *      every cold load more than an hour after sign-in would render as signed out
 *      despite a good 14-day session. A refusal ends the session; an unanswered
 *      attempt sets `sessionUnavailable` instead, because "we cannot reach the
 *      server" and "you are signed out" must not look the same to someone whose
 *      cookie is still valid.
 *   3. A useQuery on `queryKeys.currentUser` fetches `GET /me` and drives
 *      `user`. Cache invalidation (e.g. after PATCH /users/{id}/role)
 *      propagates here automatically, so RequireRole / nav visibility /
 *      useHasRole all observe role changes without manual setState.
 *   4. If `/me` fails, we branch by cause: an account-level denial (403
 *      ACCOUNT_DISABLED / ACCESS_DISABLED / ACCOUNT_NOT_PROVISIONED /
 *      ORGANIZATION_DISABLED / EMAIL_UNVERIFIED) keeps the session and shows
 *      the AccountInactive screen (re-auth can't clear it); a genuine auth
 *      failure (401 / generic 403) clears the session and bounces to /login;
 *      a transient failure (5xx / network / timeout) keeps the session and
 *      retries so a hiccup doesn't log the user out.
 *
 * No persistence layer: there is nothing to persist. The cookies survive a
 * reload on their own, and `user` is always re-fetched from `/me`, so a
 * tampered localStorage entry can no longer exist, let alone escalate a role.
 */

import {
  ACCOUNT_DENIAL_CODES,
  ApiError,
  type OrganizationGrant,
  organizationCandidatesFrom,
} from "@compliance/domain";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../lib/api";
import { isSettledSessionRefusal } from "../lib/errors";
import { writeOrgSelection } from "../lib/orgSelection";
import { queryKeys } from "../lib/queryKeys";
import { userResponseSchema } from "../lib/schemas";
import { clearMicrosoftResumeAttempt } from "./resumeMicrosoftSession";
import {
  bootstrapSession,
  completeNewPasswordChallenge,
  loginWithPassword,
  logout,
  microsoftStartUrl,
  refreshSession as refreshSessionRequest,
} from "./session";
import {
  setAuthenticated,
  setCsrfToken,
  setDenialFn,
  setRefreshFn,
  setResyncFn,
} from "./sessionBridge";
import type {
  AccountDenial,
  AuthContextValue,
  LoginOutcome,
  NewPasswordChallenge,
  Session,
  SessionProvider,
} from "./types";

// biome-ignore lint/style/noNonNullAssertion: this is the standard React context pattern
export const AuthContext = createContext<AuthContextValue>(null!);

/**
 * The auth API's own terminal rejections, on which a cached provider marker must be
 * dropped.
 *
 * Deliberately **not** `ACCOUNT_DENIAL_CODES`, which is the *product* denial set and
 * differs in both directions. A terminal refusal answers with a Problem document
 * carrying no session fields, so the API's cookie clear is invisible to this client
 * — and a stale marker keeps routing the browser to `/start` to be refused again for
 * as long as the document lives.
 *
 * `IDENTITY_NOT_PROVISIONED`, not `ACCOUNT_NOT_PROVISIONED`: the same rejection
 * wears different names by design. The product authorizers mean "no product access
 * row"; at the auth boundary nothing resolved at all and there is no session to have
 * an account in. Clearing on the product spelling alone would keep a stale marker
 * through exactly the refusal this is about.
 *
 * `ACCESS_DISABLED` is absent for a different reason, not the mirror-image one. It is
 * an account-level denial like the others — `ACCOUNT_DENIAL_CODES` carries it to the
 * same terminal screen — so calling it action-scoped would be wrong. What it does not
 * touch is the *sign-in family*, which is the marker's only subject: their Microsoft
 * identity is still how this person signs in; it is their compliance access that was
 * revoked. And being compliance's own vocabulary, it is a code the auth API never
 * clears its marker cookie for, so forgetting locally would only desynchronize this
 * copy from the one the server still holds.
 */
const AUTH_TERMINAL_CODES: ReadonlySet<string> = new Set([
  "IDENTITY_NOT_PROVISIONED",
  "ACCOUNT_DISABLED",
  "ORGANIZATION_DISABLED",
  "EMAIL_UNVERIFIED",
  "TENANT_NOT_REGISTERED",
]);

/**
 * Map an error to the organization choice it is asking for, or null.
 *
 * Kept separate from `accountDenialFromError` because the two lead to opposite
 * screens: a denial is terminal and offers a sign-out, this one is a question
 * with a list of answers.
 */
function organizationSelectionFromError(error: unknown): OrganizationGrant[] | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code !== "ORGANIZATION_SELECTION_REQUIRED") return null;
  return organizationCandidatesFrom(error.details);
}

/** Map a /me error to an account denial, or null if it isn't one. */
function accountDenialFromError(error: unknown): AccountDenial | null {
  if (error instanceof ApiError && ACCOUNT_DENIAL_CODES.has(error.code)) {
    return { code: error.code, message: error.message };
  }
  return null;
}

/**
 * A genuine authentication failure — the session itself is rejected (401) or
 * generically forbidden (403 FORBIDDEN) — where re-authenticating can help.
 * This is the only case that clears the session and bounces to /login. Account
 * denials show the AccountInactive screen; transient failures (5xx / network /
 * timeout / contract violation) keep the session and retry.
 */
function isAuthFailure(error: unknown): boolean {
  return error instanceof ApiError && (error.httpStatus === 401 || error.code === "FORBIDDEN");
}

/**
 * Any 4xx (auth failures, account denials, validation errors, ...) or
 * CONTRACT_VIOLATION — deterministic /me failures where retrying or polling
 * again can't change the outcome. Shared by the identity query's `retry` and
 * `refetchInterval` so the two can't drift out of sync the way they once did
 * (refetchInterval only excluded CONTRACT_VIOLATION, so every account-denial
 * 403 polled every 15s indefinitely). RATE_LIMITED (429) is excluded — it's
 * explicitly "retry shortly", the same carve-out already made for the RFS job
 * poll's terminal-failure check.
 */
function isDeterministicIdentityError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === "RATE_LIMITED") return false;
  return (error.httpStatus >= 400 && error.httpStatus < 500) || error.code === "CONTRACT_VIOLATION";
}

/**
 * How many times boot spends the refresh cookie before giving up, and how long it
 * waits between attempts. Small and bounded: this runs before the app renders, so
 * the budget is what a user will sit through, not what an outage might need.
 */
const BOOT_REFRESH_ATTEMPTS = 3;
const BOOT_REFRESH_BACKOFF_MS = [400, 1200];

/** An authenticated session is one the API says carries a usable credential. */
function isSignedIn(session: Session | null): boolean {
  return session?.state === "authenticated";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  // Kept outside `session`, which is null unless authenticated - and an
  // unauthenticated caller is the only one this answers a question for.
  const [lastProvider, setLastProvider] = useState<SessionProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // A session refusal the transport saw on some other route. Held here rather than
  // derived from a query, because the query that met it is not the one whose
  // failure means anything.
  const [routeDenial, setRouteDenial] = useState<AccountDenial | null>(null);
  // Same shape of state, different question — see `organizationSelection`.
  const [routeOrganizationSelection, setRouteOrganizationSelection] = useState<
    OrganizationGrant[] | null
  >(null);
  // True when a `refreshable` session could not be renewed for want of an answer,
  // as opposed to having been refused. The distinction is the difference between
  // "you are signed out" and "we cannot reach the server", and only one of those
  // is honest to show someone whose refresh cookie is still valid.
  const [sessionUnavailable, setSessionUnavailable] = useState<"renewal" | "bootstrap" | null>(
    null,
  );

  // Hold the latest session in a ref so callbacks can read it without being
  // re-created on every session change.
  const sessionRef = useRef<Session | null>(null);

  /**
   * Adopt an API answer as the current session.
   *
   * Every one of `/session`, `/login`, and `/refresh` returns the same shape,
   * so they share this one path — including publishing the CSRF token, which
   * rotates on login and refresh and would otherwise go stale and turn the
   * next mutation into a 403.
   */
  const adopt = useCallback((next: Session): Session => {
    const authenticated = next.state === "authenticated";
    setSession(authenticated ? next : null);
    sessionRef.current = authenticated ? next : null;
    // Publish the token even for an anonymous session: it is exactly what the
    // CSRF-gated login POST needs.
    setCsrfToken(next.csrfToken);
    // Same reasoning, and the same reason it is not read off `session`: the marker
    // outlives the credentials, so the answer that carries it is precisely the one
    // `session` is about to be set to null for.
    //
    // Recorded unconditionally, including a null. Every answer reports it -
    // bootstrap, login and refresh alike - so a null means the marker was cleared
    // (a sign-out, or a terminal refusal), not "this endpoint does not say". A
    // client that only ever wrote a truthy value could never observe a clear, and
    // would go on offering to resume a session the API has already disowned.
    setLastProvider(next.lastProvider);
    // Tells the HTTP client that a later 401 means "the access cookie lapsed",
    // not "never signed in" — the two are the same response otherwise.
    setAuthenticated(authenticated);
    if (authenticated) {
      setSessionUnavailable(null);
      // A new session supersedes a refusal seen on an earlier one.
      setRouteDenial(null);
      setRouteOrganizationSelection(null);
    }
    return next;
  }, []);

  const clearSession = useCallback(() => {
    setSession(null);
    sessionRef.current = null;
    setAuthenticated(false);
    queryClient.removeQueries({ queryKey: queryKeys.currentUser });
  }, [queryClient]);

  // Canonical user from /me. Driven by TanStack Query so cache invalidation
  // (e.g. after PATCH /users/{id}/role) propagates here automatically and
  // downstream consumers (RequireRole, nav, useHasRole) observe role changes
  // without manual setState wiring.
  const userQuery = useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: ({ signal }) => apiFetch("/me", userResponseSchema, { signal }),
    enabled: isSignedIn(session),
    staleTime: Number.POSITIVE_INFINITY,
    // Retry only transient failures. 4xx (auth failures + account denials) and
    // contract violations are deterministic — retrying won't change the outcome
    // (mirrors the client-wide guard in lib/queryClient.ts).
    retry: (failureCount, error) => {
      if (isDeterministicIdentityError(error)) return false;
      return failureCount < 3;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
    // After the transient-retry budget is spent, keep polling so the app
    // self-heals when the BE returns (RequireAuth holds the verify spinner
    // meanwhile) instead of stranding the user until a manual reload.
    // Also poll when a refetch failed on a query that still has data: TanStack
    // leaves status "success" there and surfaces the failure only via
    // fetchFailureReason, so a status-only check would never re-poll to recover.
    // Deterministic failures (any 4xx, including account denials, or
    // CONTRACT_VIOLATION) are excluded the same way `retry` excludes them
    // above — polling one never recovers, only adds load during the outage
    // (potentially from every affected user during an org suspension) and
    // periodically flickers the denial/contract-error screen back to a loader
    // while a refetch is in flight.
    refetchInterval: (query) => {
      const err = query.state.error ?? query.state.fetchFailureReason;
      if (isDeterministicIdentityError(err)) return false;
      return query.state.status === "error" || query.state.fetchFailureReason !== null
        ? 15_000
        : false;
    },
    refetchOnWindowFocus: true,
  });
  // TanStack keeps status "success" (and error null) when a refetch fails on a
  // query that already has data, so consult failureReason too — otherwise a
  // stale identity would slip through unnoticed.
  const identityError = userQuery.error ?? userQuery.failureReason ?? null;
  // Suppress the cached user whenever the latest /me fetch failed, so route
  // guards hold the verify spinner rather than render a stale role (e.g. a
  // self-demoted admin whose identity refetch then failed). The session is kept
  // (see the effect below), so this holds rendering rather than logging out.
  const user = identityError ? null : (userQuery.data ?? null);
  // Non-null when the session was refused - by `/me`, or by any other route.
  //
  // The second source is not a nicety. These codes refuse the *caller*, not the
  // request, but they arrive on whichever route ran next, and a tenant
  // de-registered mid-session is checked per request. Read from `/me` alone the
  // refusal would go unseen until something invalidated it, and `/me` holds
  // `staleTime: Infinity` - so the user would sit inside the protected UI watching
  // every request fail with nothing on screen to say why.
  const accountDenial = accountDenialFromError(identityError) ?? routeDenial;
  // True only when /me succeeded on the wire but failed schema validation — a
  // version skew, not a transient failure (mirrors the retry guard above,
  // which already never retries this deterministically).
  const identityContractError =
    identityError instanceof ApiError && identityError.code === "CONTRACT_VIOLATION";
  // Any OTHER deterministic failure (e.g. an unexpected 404/422 not in the
  // known denial list) — isDeterministicIdentityError already stops retry and
  // polling for these above, so without an explicit screen the session is
  // kept, user stays suppressed, and RequireAuth/Login are stuck on the
  // verify spinner forever with no recovery. isAuthFailure is excluded: that
  // already clears the session and bounces to /login via the effect below.
  // The caller holds grants in several organizations and has not chosen one.
  // Answerable, so it is NOT an account denial: the app renders a picker and
  // the same session proceeds once a choice is made. Read from `/me` and from
  // any other route for the same reason `accountDenial` is — with `/me` held at
  // `staleTime: Infinity`, a refusal that first appears elsewhere would
  // otherwise leave the user inside the UI watching every request fail.
  const organizationSelection =
    organizationSelectionFromError(identityError) ?? routeOrganizationSelection;
  // Any OTHER deterministic failure (e.g. an unexpected 404/422 not in the
  // known denial list) — isDeterministicIdentityError already stops retry and
  // polling for these above, so without an explicit screen the session is
  // kept, user stays suppressed, and RequireAuth/Login are stuck on the
  // verify spinner forever with no recovery. isAuthFailure is excluded: that
  // already clears the session and bounces to /login via the effect below.
  const identityUnhandledError =
    isDeterministicIdentityError(identityError) &&
    !accountDenial &&
    !organizationSelection &&
    !identityContractError &&
    !isAuthFailure(identityError);

  // Boot: ask the API what session the cookies represent, and spend the one
  // refresh attempt a `refreshable` answer calls for. Both happen before
  // `loading` drops, so route guards never see the intermediate "no session
  // yet, but one is coming" state and bounce a signed-in user to /login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bootstrapped = await bootstrapSession();
        if (cancelled) return;
        if (bootstrapped.state !== "refreshable") {
          adopt(bootstrapped);
          return;
        }
        // Optimistic by design: bootstrap is a GET and never contacts Cognito,
        // so the refresh cookie's validity is unknown until we spend it here.
        //
        // Retried, because a single attempt conflates two very different answers.
        // Only a settled refusal means the session is over; a 5xx or a network
        // drop says nothing about the cookie, and a user holding a good 14-day
        // session must not be shown a sign-in form because the API blinked.
        let lastError: unknown = null;
        for (let attempt = 0; attempt < BOOT_REFRESH_ATTEMPTS; attempt += 1) {
          try {
            const refreshed = await refreshSessionRequest(bootstrapped.csrfToken);
            if (!cancelled) adopt(refreshed);
            return;
          } catch (err) {
            if (cancelled) return;
            lastError = err;
            if (isSettledSessionRefusal(err)) {
              // Expired or revoked. Keep the bootstrap answer's CSRF token so the
              // login form can post with it.
              adopt({ ...bootstrapped, state: "anonymous" });
              return;
            }
            if (attempt < BOOT_REFRESH_ATTEMPTS - 1) {
              await new Promise((resolve) => setTimeout(resolve, BOOT_REFRESH_BACKOFF_MS[attempt]));
              if (cancelled) return;
            }
          }
        }
        // Every attempt failed without a verdict. Nothing is adopted, so no
        // request pretends to carry a session — but the caller must not be told
        // they are signed out either, so this reports an outage the UI renders
        // instead of the credentials form.
        setSessionUnavailable("renewal");
        setError(lastError instanceof Error ? lastError : new Error(String(lastError)));
      } catch (err) {
        // The bootstrap itself failed — the API is unreachable, or refused a GET
        // that answers 200 even for anonymous callers. Either way we do not know
        // what session exists, which is the same not-knowing the retry loop above
        // reports, so it surfaces the same way rather than as a sign-in prompt.
        if (!cancelled) {
          setSessionUnavailable("bootstrap");
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adopt]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      setError(null);
      // The CSRF token from bootstrap — the anonymous answer carries one
      // precisely so this call can be made.
      const csrf = sessionRef.current?.csrfToken ?? (await bootstrapSession()).csrfToken;
      const outcome = await loginWithPassword(email, password, csrf);
      // A challenge is not a session: nothing is adopted, no cookies exist yet,
      // and the caller has to collect a replacement password first.
      if (outcome.kind === "session") {
        adopt(outcome.session);
        // Identity is cached with staleTime: Infinity, so drop any previous row
        // to force /me to re-fire as the newly signed-in user.
        queryClient.removeQueries({ queryKey: queryKeys.currentUser });
      }
      return outcome;
    },
    [adopt, queryClient],
  );

  const completeNewPassword = useCallback(
    async (
      challenge: NewPasswordChallenge,
      newPassword: string,
      attributes?: Record<string, string>,
    ) => {
      setError(null);
      adopt(await completeNewPasswordChallenge(challenge, newPassword, attributes));
      queryClient.removeQueries({ queryKey: queryKeys.currentUser });
    },
    [adopt, queryClient],
  );

  const signInWithMicrosoft = useCallback(() => {
    setError(null);
    // A whole-window navigation: the API drives the handshake and redirects
    // back here already signed in, so there is nothing to await and no
    // callback route in this app.
    window.location.assign(microsoftStartUrl());
  }, []);

  const signOut = useCallback(async () => {
    // Clear any stale auth error so a recovered outage isn't rendered as a
    // failed sign-in on the login screen after sign-out.
    setError(null);
    const csrf = sessionRef.current?.csrfToken;
    if (!csrf) {
      clearSession();
      return;
    }

    // Only the API can end this session: the access and refresh cookies are
    // HttpOnly, so no amount of local clearing removes them. Clearing anyway
    // would render a signed-out UI over a live session that any reload restores
    // — the failure mode that matters at a shared workstation, where the user
    // walks away *because* the screen says they are signed out.
    //
    // So a failed sign-out is reported as failed, and the session is kept.
    let logoutUrl: string | null = null;
    try {
      ({ logoutUrl } = await logout(csrf));
    } catch (err) {
      setError(
        new Error(
          "Sign-out failed, so you are still signed in. Check your connection and try again.",
          { cause: err },
        ),
      );
      throw err;
    }

    // Synchronously, and before `clearSession`: the re-bootstrap below awaits, React
    // flushes during it, and `RequireAuth` gets a render in which the caller is
    // unauthenticated and the marker still names Microsoft. That is enough to hand
    // the window to `/start` and lose the `logoutUrl` navigation four lines down —
    // leaving Entra's session live for `prompt=select_account` to re-admit the same
    // user in a click, which is the shared-workstation failure that navigation
    // exists to prevent. Dropping it after the await is too late by exactly that
    // gap, so this cannot move.
    //
    // Not folded into `clearSession`, which the lapsed-session path also calls and
    // where the marker must survive — that case is the entire reason it exists. An
    // explicit sign-out is the one place forgetting is right, which is why the API
    // clears the cookie here too and nowhere else.
    setLastProvider(null);
    // And the denial, which `clearSession` does not hold. Left set, `AccountInactive`
    // renders again on the `/login` this lands on - so where `/logout` returns no
    // provider URL, the next person at this browser cannot reach the sign-in form at
    // all, and further sign-out clicks have no session left to change that.
    setRouteDenial(null);
    setRouteOrganizationSelection(null);
    clearSession();
    // Re-bootstrap for a fresh CSRF token so the login form works without a
    // reload. Best-effort: the local sign-out above already happened.
    //
    // Only the token is read off the answer, not `adopt`-ed: the marker is already
    // null above, and it must be, because adopting happens after the await this is
    // guarding against.
    try {
      setCsrfToken((await bootstrapSession()).csrfToken);
    } catch {
      setCsrfToken(null);
    }
    // End the IdP's own session too. Without this the Hosted UI cookie
    // survives and the next "Sign in with Microsoft" silently re-authenticates
    // as the same user, defeating the sign-out.
    if (logoutUrl) window.location.assign(logoutUrl);
  }, [clearSession]);

  /**
   * Re-read `/session` for the provider marker alone.
   *
   * Deliberately not `adopt`: this runs when the session has just ended, and the
   * answer can still say `authenticated` - the cookie is valid, it is `/me` that
   * refused. Adopting it re-enables the identity query that just failed, which
   * fails again, which clears and re-reads, and the client cycles between `/me` and
   * `/session` with the protected UI stuck on its loader. The only thing wanted here
   * is whose marker the server holds, so a sign-out in another tab is honoured
   * instead of resumed over.
   *
   * The CSRF token comes along because sign-out rotates nothing and the login form
   * this leads to is itself CSRF-gated.
   */
  const revalidateProvider = useCallback(async (): Promise<void> => {
    // `loading` is raised for the round trip so `RequireAuth` holds its loader rather
    // than deciding on the stale marker - the decision is taken in a render body, so
    // there is no awaiting it. Part of what the re-read *is*, so it lives here rather
    // than at each caller. An async body runs synchronously to its first `await`, so
    // this still lands in the same window it did when the callers set it.
    setLoading(true);
    try {
      const next = await bootstrapSession();
      setLastProvider(next.lastProvider);
      setCsrfToken(next.csrfToken);
    } catch {
      // An unanswered re-read establishes nothing, so the cached marker stands. The
      // one-attempt bound already stops a resume on it from repeating.
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSession = useCallback(async (): Promise<Session> => {
    const csrf = sessionRef.current?.csrfToken ?? (await bootstrapSession()).csrfToken;
    try {
      return adopt(await refreshSessionRequest(csrf));
    } catch (err) {
      // A refused refresh has to end the session *here*, not only where `/me`
      // notices. `/me` is cached with staleTime: Infinity, so while the app stays
      // mounted it never refetches — a refused refresh behind some mutation would
      // otherwise leave the protected UI on screen with every action failing, and
      // no recovery until a window-focus refetch or a reload.
      //
      // Transient failures and rate limiting deliberately do not clear: nothing
      // was learned about the cookie, and the caller retries.
      if (isSettledSessionRefusal(err)) {
        clearSession();
        // The same re-read the `/me` path does, because this path reaches the guard
        // without ever setting `identityError`: another tab's sign-out surfaces here
        // as a refused refresh, and the marker this tab still holds would resume the
        // session that tab just ended.
        void revalidateProvider();
      }
      throw err;
    }
  }, [adopt, clearSession, revalidateProvider]);

  /**
   * Re-read the session to pick up a CSRF token another tab's refresh rotated.
   *
   * Two tabs share one session but not one bridge: when either refreshes, the API
   * rotates the token and only that tab's module-local copy is updated. The other
   * tab's next mutation is then a 403 CSRF_FAILED, and would stay broken until a
   * reload, because the 401 path above never fires for it.
   */
  const resyncSession = useCallback(async (): Promise<Session> => {
    return adopt(await bootstrapSession());
  }, [adopt]);

  // Register both handlers with the bridge so lib/api can reach them from a
  // queryFn closure, where hooks are unavailable.
  useEffect(() => {
    setRefreshFn(refreshSession);
    setResyncFn(resyncSession);
    return () => {
      setRefreshFn(null);
      setResyncFn(null);
    };
  }, [refreshSession, resyncSession]);

  // The third handler, registered apart because it flows the other way: the two
  // above are things the transport asks us to do, this is something it tells us.
  useEffect(() => {
    setDenialFn((code, message, details) => {
      // A question, not a refusal: it takes the picker, not the denial screen,
      // and must not clear the provider marker — the session is fine.
      if (code === "ORGANIZATION_SELECTION_REQUIRED") {
        setRouteOrganizationSelection(organizationCandidatesFrom(details));
        return;
      }
      setRouteDenial({ code, message });
      // These answers are Problem documents, carrying no session fields, so the
      // API's clearing of its marker cookie is invisible here. Without dropping the
      // cached copy the browser goes on resuming into the same refusal.
      if (AUTH_TERMINAL_CODES.has(code)) setLastProvider(null);
    });
    return () => setDenialFn(null);
  }, []);

  // The resume guard is armed here, and deliberately not when `/session` reports a
  // session: `/session` saying `authenticated` is not the same as being admitted.
  // If `/me` then refuses with a plain 401 or `FORBIDDEN` - neither of which clears
  // the marker - the session is dropped, the marker stands, and the guard would
  // have just been re-armed, so `RequireAuth` resumes into the same refusal and
  // every callback re-arms it again. The bound would never bind. Arming on a `/me`
  // that actually succeeded is what makes one attempt mean one attempt.
  useEffect(() => {
    if (userQuery.data) clearMicrosoftResumeAttempt();
  }, [userQuery.data]);

  // `session.accessExpiresAt` is deliberately unread.
  //
  // It is tempting: the API states exactly when the access token dies, so a
  // timer could renew a minute early and skip the one 401-and-retry each session
  // spends per hour. That was built, reviewed, and withdrawn — the round trip is
  // invisible to users, and buying it back costs more than it is worth.
  //
  // Two hazards, both in the auth path:
  //
  //   1. Sign-out race. A renewal in flight when `signOut` completes lands
  //      afterwards, applies its `Set-Cookie` headers and adopts an
  //      authenticated session — undoing a sign-out that already reported
  //      success. That is exactly the shared-workstation failure `signOut`
  //      below is written to prevent, arrived at from the other direction.
  //      Closing it needs a session epoch, an AbortController, and a re-issued
  //      logout for a response that lands anyway.
  //   2. Cross-tab stampede. Every tab holds the same expiry, so every tab
  //      schedules the same instant. The bridge single-flights per module, not
  //      across tabs, and each refresh rotates the shared CSRF token — the
  //      others then fail `CSRF_FAILED` faster than recovery tolerates. Closing
  //      it needs jitter or `BroadcastChannel`.
  //
  // The 401-and-retry path in `lib/api.ts` is the mechanism, it is verified in
  // production, and it costs one invisible round trip an hour. Leave it.

  // React to a /me failure by cause:
  //   - Account denial (disabled / not-provisioned / org-disabled / email-
  //     unverified): keep the session. RequireAuth shows AccountInactive and
  //     the user can deliberately sign out (ending the Hosted UI session).
  //     Clearing + /login would let the IdP cookie silently re-login into the
  //     same failure.
  //   - Contract violation (the response failed schema validation — a version
  //     skew, not an access decision): same treatment as account denial. It's
  //     deterministic, so re-authenticating or waiting it out won't help;
  //     RequireAuth shows a dedicated screen with sign-out instead of holding
  //     the verify spinner forever.
  //   - Genuine auth failure (401 / generic 403 FORBIDDEN): fail closed —
  //     clear the session so route guards bounce to /login.
  //   - Transient (5xx / network / timeout): keep the session; the query keeps
  //     retrying (RequireAuth holds the spinner) so a backend hiccup doesn't
  //     log the user out.
  useEffect(() => {
    if (!identityError) return;
    setError(identityError instanceof Error ? identityError : new Error(String(identityError)));
    // Before the denial return below, because most of these *are* denials: the API
    // has cleared its marker cookie and told us only through a Problem document,
    // which carries no session fields, so this is the one signal we get.
    if (identityError instanceof ApiError && AUTH_TERMINAL_CODES.has(identityError.code)) {
      setLastProvider(null);
    }
    if (accountDenial) return;
    if (!isAuthFailure(identityError)) return;
    clearSession();
    // And re-read `/session`, because the marker we hold may already be wrong. A
    // sign-out in another tab clears the host cookies for every tab, but only the
    // tab that ran it drops its own cached marker; this one still reads
    // `"microsoft"`, and would resume - re-seating a session the user just ended,
    // if it wins the race against that tab's provider sign-out.
    //
    // `loading` is raised for the round trip so `RequireAuth` holds its loader
    // instead of deciding on the stale value. That is the same thing `loading`
    // already means - "we are asking the API whether a session exists" - and the
    // decision this guards is taken in a render body, so there is no awaiting it.
    void revalidateProvider();
  }, [identityError, accountDenial, clearSession, revalidateProvider]);

  // Clear a stale error only once /me actually SUCCEEDS again (fresh data, no
  // error) — not merely when identityError goes null, which also happens when
  // an auth-failure logout removes the query. Keeping that distinction
  // preserves the 401 error so LoginPage can show its "session expired"
  // message.
  useEffect(() => {
    if (userQuery.data && !identityError) setError(null);
  }, [userQuery.data, identityError]);

  /**
   * Switch the organization this tab acts in.
   *
   * Returns false without switching while a mutation is in flight. The reset
   * below would throw that response away, and a discarded sign-off or approval
   * is invisible — the user sees a spinner stop with nothing to say whether it
   * landed. Waiting is the only safe answer, so the caller reports it.
   */
  const selectOrganization = useCallback(
    (organizationId: string): boolean => {
      if (queryClient.isMutating() > 0) return false;
      writeOrgSelection(organizationId);
      setRouteOrganizationSelection(null);
      // `resetQueries`, and neither `invalidateQueries` nor `clear`.
      //
      // `invalidateQueries` only marks entries stale, so an inactive one a
      // later mount reads would still render the previous organization's rows
      // before its refetch lands — which is why it was rejected here.
      //
      // But `clear` alone did nothing the user could see. It deletes the cache
      // entries and destroys the `Query` objects, and destruction notifies
      // nobody: a mounted `QueryObserver` holds its own reference to the
      // destroyed query and keeps reporting that query's last data. Nothing
      // re-renders, so no observer ever calls `setOptions` to rebuild against
      // the now-empty cache, and no request is made. The switch took effect
      // only on a manual reload. `setRouteOrganizationSelection(null)` does not
      // rescue it either: outside the picker the value is already `null`, so
      // React bails out of the re-render. That is exactly why switching from
      // the `ORGANIZATION_SELECTION_REQUIRED` screen worked while switching
      // from the user menu did not.
      //
      // `resetQueries` is the pair that does both halves: every query, active
      // or not, drops back to its initial state — so nothing can render the old
      // organization's rows — and the mounted ones refetch immediately, now
      // carrying the new `X-Organization-Id`.
      queryClient.getMutationCache().clear();
      void queryClient.resetQueries();
      return true;
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      error,
      lastProvider,
      accountDenial,
      organizationSelection,
      selectOrganization,
      identityContractError,
      identityUnhandledError,
      sessionUnavailable,
      signIn,
      completeNewPassword,
      signInWithMicrosoft,
      signOut,
      refreshSession,
    }),
    [
      user,
      session,
      loading,
      error,
      lastProvider,
      accountDenial,
      organizationSelection,
      selectOrganization,
      identityContractError,
      identityUnhandledError,
      sessionUnavailable,
      signIn,
      completeNewPassword,
      signInWithMicrosoft,
      signOut,
      refreshSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
